import { FC, ReactNode } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Warning } from 'src/components/icons/warning';

/**
 * Field definition for a field in a FieldDisplay
 */
export interface FieldDefinition {
  label: string;
  value?: string | null;
  icon?: ReactNode;
  // This error message only works on vertical variant
  errorMessage?: string | null;
}

/**
 * FieldDisplay Component
 *
 * Displays a read-only label-value pair with optional icon.
 * Used for showing information in a structured format.
 */
export const FieldDisplay: FC<{
  field: FieldDefinition;
  variant?: 'vertical' | 'horizontal';
}> = ({ field, variant = 'vertical' }) => {
  const theme = useTheme();

  const { label, value, icon, errorMessage } = field;

  if (variant === 'horizontal') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          textAlign: 'left',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: '1',
            alignItems: 'center',
            mr: 2,
          }}
        >
          {icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mr: 1,
              }}
            >
              {icon}
            </Box>
          )}
          <Typography
            variant='subtitle2'
            color={theme.palette.text.secondary}
            data-testid='field-label'
            sx={{
              fontWeight: theme.typography.fontWeightMedium,
              textAlign: 'left',
            }}
          >
            {label}:
          </Typography>
        </Box>
        <Typography
          variant='subtitle2'
          color={theme.palette.text.primary}
          data-testid='field-value'
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
            textAlign: 'left',
            flex: '1',
            wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant='subtitle2'
        color={theme.palette.text.secondary}
        data-testid='field-label'
        sx={{
          fontWeight: theme.typography.fontWeightMedium,
        }}
      >
        {label}:
      </Typography>
      <Typography
        variant='subtitle2'
        color={theme.palette.text.primary}
        data-testid='field-value'
        sx={{
          fontWeight: theme.typography.fontWeightMedium,
        }}
      >
        {value}
      </Typography>
      {errorMessage && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            bgcolor: theme.palette.text.disabled,
            p: '4px 6px',
            borderRadius: 1,
            gap: 1,
          }}
        >
          <Warning
            fill={theme.palette.text.secondary}
            sx={{
              fontSize: (theme) => theme.typography.body1.fontSize,
            }}
          />
          <Typography
            variant='subtitle2'
            color={theme.palette.text.primary}
            data-testid='field-error-message'
            sx={{
              fontWeight: theme.typography.fontWeightRegular,
            }}
          >
            {errorMessage}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
